import { htmlEscape } from 'escape-goat';
import { getCurrentInstance } from 'vue';
import { onBeforeRouteLeave, onBeforeRouteUpdate } from 'vue-router';

/**
 * Warns a learner who tries to leave an activity while a file submission is still uploading.
 *
 * WHY THIS EXISTS (ClickUp 868ma6en4). Moving from one activity to another is a param change on
 * the same `activity-view` route record, so the activity page re-keys and unmounts the renderer
 * while its upload promise keeps running. The submission itself is pinned to the activity the
 * learner started on (see FileSubmission.vue), so leaving can no longer misfile it; this guard
 * makes sure the learner knows the upload is still running and chooses to stay or leave.
 *
 * Both hooks are required: `onBeforeRouteUpdate` covers activity-to-activity navigation (same
 * record, new `activityId`), `onBeforeRouteLeave` covers every other destination. A browser
 * refresh or tab close is covered by a `beforeunload` prompt.
 *
 * Choosing "Leave" lets the in-flight submission finish in the background for the original
 * activity. The `beforeunload` prompt stays armed until `finish()` runs, even after the component
 * unmounts, so closing the tab during that background work still warns.
 *
 * @param {Object} [options]
 * @param {Function} [options.confirm] - Receives `{ activityTitle }` and resolves `true` to leave.
 *     Defaults to the shared `$dialog` confirmation.
 * @returns {{ start: Function, finish: Function, isActive: Function }}
 */
export function useUploadInProgressGuard(options = {}) {
    const instance = getCurrentInstance();

    let activeUpload = null;
    let pendingConfirmation = null;
    let leaveConfirmed = false;

    const confirmWithDialog = ({ activityTitle }) =>
        new Promise(resolve => {
            const title = activityTitle ? `"${htmlEscape(activityTitle)}"` : 'this activity';

            instance.proxy.$dialog.confirm({
                title: 'Your upload is still in progress',
                message:
                    `Your files for ${title} are still uploading. ` +
                    `If you leave now, the upload will keep running in the background and will be submitted to ${title}. ` +
                    'Keep this browser tab open until you see a confirmation.',
                cancelText: 'Stay on this page',
                confirmText: 'Leave anyway',
                type: 'is-danger',
                onConfirm: () => resolve(true),
                onCancel: () => resolve(false),
            });
        });

    const confirm = options.confirm ?? confirmWithDialog;

    const guard = (to, from) => {
        if (!activeUpload || leaveConfirmed) return true;

        // A query/hash-only change keeps the renderer mounted, so the upload is unaffected.
        if (to?.path && from?.path && to.path === from.path) return true;

        // Repeated navigation attempts while the dialog is open share one answer.
        if (!pendingConfirmation) {
            const upload = activeUpload;
            pendingConfirmation = Promise.resolve(confirm({ activityTitle: upload.activityTitle })).then(leave => {
                pendingConfirmation = null;

                // The answer was about the upload that was running when the dialog opened. If a new
                // upload started meanwhile, cancel this navigation so the next attempt asks again.
                if (activeUpload && activeUpload !== upload) return false;
                if (leave) leaveConfirmed = true;

                return Boolean(leave);
            });
        }

        return pendingConfirmation;
    };

    const onBeforeUnload = event => {
        if (!activeUpload) return;

        event.preventDefault();
        event.returnValue = '';
    };

    onBeforeRouteLeave(guard);
    onBeforeRouteUpdate(guard);

    /**
     * Arm the guard for one submission.
     *
     * @param {Object} upload
     * @param {String} [upload.activityTitle] - Title of the activity the files belong to.
     */
    function start(upload = {}) {
        activeUpload = { activityTitle: upload.activityTitle ?? '' };
        leaveConfirmed = false;
        window.addEventListener('beforeunload', onBeforeUnload);
    }

    /** Disarm the guard once the submission has succeeded or failed. */
    function finish() {
        activeUpload = null;
        window.removeEventListener('beforeunload', onBeforeUnload);
    }

    return {
        start,
        finish,
        isActive: () => activeUpload !== null,
    };
}
